import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

export type AppLanguage = 'es' | 'en';

const STORAGE_KEY = 'appLang';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly languageSubject = new BehaviorSubject<AppLanguage>('es');
  readonly languageChanges = this.languageSubject.asObservable();

  constructor(private translate: TranslateService) {}

  async init(): Promise<void> {
    this.translate.setDefaultLang('es');
    const lang = this.getStoredLanguage();
    await firstValueFrom(this.translate.use(lang));
    this.applyLanguage(lang, false);
  }

  getCurrentLanguage(): AppLanguage {
    const current = this.translate.currentLang || this.languageSubject.value;
    return current === 'en' ? 'en' : 'es';
  }

  async setLanguage(lang: AppLanguage): Promise<void> {
    if (lang !== 'es' && lang !== 'en') {
      return;
    }
    await firstValueFrom(this.translate.use(lang));
    this.applyLanguage(lang, true);
  }

  private applyLanguage(lang: AppLanguage, persist: boolean): void {
    if (persist) {
      localStorage.setItem(STORAGE_KEY, lang);
    }
    document.documentElement.lang = lang;
    this.languageSubject.next(lang);
  }

  private getStoredLanguage(): AppLanguage {
    return localStorage.getItem(STORAGE_KEY) === 'en' ? 'en' : 'es';
  }
}
